import AOS from 'aos';
import 'aos/dist/aos.css';
import { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { useQueryClient } from '@tanstack/react-query';
import useDonations from '../hooks/useDonations';
import { toaster } from '../services/toaster';
import './Donate.css';

import 'owl.carousel/dist/assets/owl.carousel.css';
import 'owl.carousel/dist/assets/owl.theme.default.css';

function Donate() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: latestDonations = [] } = useDonations();
  const [donationAmount, setDonationAmount] = useState('10.00');
  const [isCustomMode, setIsCustomMode] = useState(false);
  const [customInputValue, setCustomInputValue] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [successName, setSuccessName] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    if (dateStr === t('donatePage.just_now') || dateStr === t('donatePage.today')) return dateStr;
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return date.toLocaleDateString();
  };

  const donationAmounts = [
    { value: '5.00', key: 'donation_5' },
    { value: '10.00', key: 'donation_10' },
  ];

  const donate = [
    {
      icon: 'flaticon-animals-9',
      title: 'donatePage.food',
      description: 'donatePage.food_description',
    },
    {
      icon: 'flaticon-dog-with-first-aid-kit-bag',
      title: 'donatePage.medical',
      description: 'donatePage.medical_description',
    },
    {
      icon: 'flaticon-pet-shelter',
      title: 'donatePage.shelter',
      description: 'donatePage.shelter_description',
    },
    {
      icon: 'flaticon-dog',
      title: 'donatePage.support',
      description: 'donatePage.support_description',
    },
  ];

  const carouselRef = useRef(null);

  useEffect(() => {
    AOS.init();

    if (window.$ && carouselRef.current) {
      window.$(carouselRef.current).owlCarousel({
        loop: false,
        margin: 20,
        nav: true,
        dots: true,
        autoplay: false,
        autoplayTimeout: 4000,
        navText: [
          "<i class='fas fa-chevron-left'></i>",
          "<i class='fas fa-chevron-right'></i>",
        ],
        responsive: {
          0: { items: 1 },
          768: { items: 2 },
          992: { items: 3 },
          1200: { items: 4 },
        },
      });
    }
  }, []);

  return (
    <div>
      <div id="services" className="container">
        <div className="container block-padding pt-0">
          <div className="row g-4 justify-content-center">
            {/* MIDDLE BOX — bank details */}
            <div className="col-xl-4 col-lg-4 mb-4 text-center">
              <div className="pf-bento-card">
                <i className="fas fa-university pf-bento-icon"></i>
                <h3 style={{ fontWeight: '700' }}>
                  {t('donatePage.deposit_title')}
                </h3>
                <p className="fw-bold mt-2 mb-1">
                  {t('donatePage.deposit_description')}
                </p>
                <p className="text-muted mb-3" style={{ fontSize: '1.1rem' }}>
                  Račun Br:{' '}
                  <span style={{ color: '#000', fontWeight: 'bold' }}>
                    3387302277034937
                  </span>
                </p>
                <p
                  className="text-muted text-center"
                  style={{ fontSize: '0.9rem' }}
                >
                  {t('donatePage.bank_details')} <br />
                  {t('donatePage.address')}: Bistrik 23a, Sarajevo
                </p>
              </div>
            </div>

            {/* LEFT BOX — PayPal DONATE */}
            <div className="col-xl-4 col-lg-4 mb-4">
              <div className="pf-bento-card">
                <i
                  className="fab fa-paypal pf-bento-icon"
                  style={{ color: '#003087' }}
                ></i>
                <h3 style={{ fontWeight: '700' }}>Paypal</h3>
                <p className="text-muted mb-1">pawfriendssarajevo@gmail.com</p>
                <p className="text-muted mb-4">handmadehelp@gmx.at</p>

                <div style={{ marginTop: '20px' }}>
                  {/* Donation Amount Selector */}
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginBottom: '20px' }}>
                    {donationAmounts.map(amount => (
                      <button
                        key={amount.value}
                        onClick={() => {
                          setDonationAmount(amount.value);
                          setIsCustomMode(false);
                        }}
                        style={{
                          padding: '10px 20px',
                          border: (!isCustomMode && donationAmount === amount.value) ? '2px solid #fca311' : '2px solid #ddd',
                          borderRadius: '20px',
                          backgroundColor: (!isCustomMode && donationAmount === amount.value) ? '#fff3db' : '#fff',
                          color: (!isCustomMode && donationAmount === amount.value) ? '#fca311' : '#666',
                          fontWeight: (!isCustomMode && donationAmount === amount.value) ? '700' : '600',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          fontSize: '14px',
                        }}
                        onMouseEnter={(e) => {
                          if (isCustomMode || donationAmount !== amount.value) {
                            e.target.style.borderColor = '#fca311';
                            e.target.style.backgroundColor = '#fff9f0';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (isCustomMode || donationAmount !== amount.value) {
                            e.target.style.borderColor = '#ddd';
                            e.target.style.backgroundColor = '#fff';
                          }
                        }}
                      >
                        {t(`donatePage.${amount.key}`)}
                      </button>
                    ))}

                    {/* Other Amount Button */}
                    <button
                      onClick={() => {
                        setIsCustomMode(true);
                        setDonationAmount(customInputValue || '0.00');
                      }}
                      style={{
                        padding: '10px 20px',
                        border: isCustomMode ? '2px solid #fca311' : '2px solid #ddd',
                        borderRadius: '20px',
                        backgroundColor: isCustomMode ? '#fff3db' : '#fff',
                        color: isCustomMode ? '#fca311' : '#666',
                        fontWeight: isCustomMode ? '700' : '600',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        fontSize: '14px',
                      }}
                      onMouseEnter={(e) => {
                        if (!isCustomMode) {
                          e.target.style.borderColor = '#fca311';
                          e.target.style.backgroundColor = '#fff9f0';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isCustomMode) {
                          e.target.style.borderColor = '#ddd';
                          e.target.style.backgroundColor = '#fff';
                        }
                      }}
                    >
                      {t('donatePage.donation_other')}
                    </button>
                  </div>

                  {/* Custom Amount Input */}
                  {isCustomMode && (
                    <div style={{ marginBottom: '20px', animation: 'fadeIn 0.3s ease' }}>
                      <div style={{ position: 'relative', width: '150px', margin: '0 auto' }}>
                        <span style={{
                          position: 'absolute',
                          left: '15px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: '#666',
                          fontWeight: '600'
                        }}>€</span>
                        <input
                          type="number"
                          min="1"
                          step="0.01"
                          value={customInputValue}
                          placeholder="0.00"
                          onChange={(e) => {
                            const val = e.target.value;
                            setCustomInputValue(val);
                            // Only update actual donation amount if it's a valid number
                            if (!isNaN(parseFloat(val)) && parseFloat(val) > 0) {
                              setDonationAmount(parseFloat(val).toFixed(2));
                            } else {
                              setDonationAmount('0.00');
                            }
                          }}
                          style={{
                            width: '100%',
                            padding: '10px 10px 10px 30px',
                            borderRadius: '15px',
                            border: '2px solid #fca311',
                            fontSize: '16px',
                            fontWeight: '600',
                            textAlign: 'center',
                            outline: 'none',
                            boxShadow: '0 4px 10px rgba(252, 163, 17, 0.1)'
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Anonymous Checkbox */}
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '25px', marginTop: '10px' }}>
                    <button
                      onClick={() => setIsAnonymous(!isAnonymous)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '10px 25px',
                        border: isAnonymous ? '2px solid #fca311' : '2px solid #ddd',
                        borderRadius: '30px',
                        backgroundColor: isAnonymous ? '#fff3db' : '#fff',
                        color: isAnonymous ? '#fca311' : '#666',
                        fontWeight: '700',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        fontSize: '15px',
                        boxShadow: isAnonymous ? '0 4px 10px rgba(252, 163, 17, 0.15)' : 'none'
                      }}
                      onMouseEnter={(e) => {
                        if (!isAnonymous) {
                          e.currentTarget.style.borderColor = '#fca311';
                          e.currentTarget.style.backgroundColor = '#fff9f0';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isAnonymous) {
                          e.currentTarget.style.borderColor = '#ddd';
                          e.currentTarget.style.backgroundColor = '#fff';
                        }
                      }}
                    >
                      <i className={`fas ${isAnonymous ? 'fa-user-secret' : 'fa-user'}`}></i>
                      {t('donatePage.donate_anonymously')}
                    </button>
                  </div>

                  <PayPalScriptProvider
                    options={{
                      'client-id':
                        'AVGkSsiHM_n-zWJ5v7Cl88nkfEFG0gZ3gR0p8ASf2T4g-Cj2Cm1bZLVvBHNGP55ViyW4Xmk9s3nQFQJn',
                      currency: 'EUR',
                    }}
                  >
                    {/* PayPal Donate Button */}
                    <div style={{ width: '220px', margin: '0 auto' }}>
                      <PayPalButtons
                        key={donationAmount}
                        style={{
                          layout: 'vertical',
                          color: 'gold',
                          shape: 'pill',
                          label: 'donate',
                        }}
                        createOrder={(data, actions) => {
                          return actions.order.create({
                            purchase_units: [
                              {
                                amount: { value: donationAmount },
                              },
                            ],
                          });
                        }}
                        onApprove={(data, actions) => {
                          return actions.order.capture().then(async details => {
                            const firstName = details.payer.name.given_name || '';
                            const lastName = details.payer.name.surname || '';
                            const fullName = `${firstName} ${lastName}`.trim();

                            const displayName = isAnonymous ? 'anonymous' : fullName;

                            // We can still thank them personally by their real name in the success modal
                            setSuccessName(fullName);
                            setShowSuccess(true);

                            const newDonation = { name: displayName, amount: donationAmount, date: new Date().toISOString() };

                            queryClient.setQueryData(['donations'], (prev = []) => [
                              { ...newDonation, date: t('donatePage.just_now') },
                              ...prev,
                            ]);

                            // Send to backend
                            const endpoint = import.meta.env.VITE_PAWFRIENDS_API_ENDPOINT_CREATE_DONATION;
                            if (endpoint) {
                              try {
                                const response = await fetch(endpoint, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify(newDonation)
                                });
                                if (!response.ok) {
                                  toaster.showError(t('donatePage.donation_save_error'));
                                }
                              } catch (err) {
                                console.error("Failed to save donation", err);
                                toaster.showError(t('donatePage.donation_save_error'));
                              }
                            }

                            setTimeout(() => setShowSuccess(false), 5000);
                          });
                        }}
                      />
                    </div>
                  </PayPalScriptProvider>
                </div>
              </div>
            </div>

            {/* RIGHT BOX — Latest Donations */}
            <div className="col-xl-4 col-lg-4 mb-4">
              <div className="pf-bento-card" style={{ justifyContent: 'flex-start', textAlign: 'center' }}>
                <i className="fas fa-heart pf-bento-icon" style={{ color: '#ff4d4d' }}></i>
                <h5 style={{ fontWeight: '700', marginBottom: '25px', color: '#666' }}>{t('donatePage.latest_donations')}</h5>

                <div className="pf-donation-list">
                  {latestDonations.map((donation, idx) => (
                    <div key={idx} className="pf-donation-item" style={{ animationDelay: `${idx * 0.1}s` }}>
                      <span className="pf-donor-name">
                        <i className="fas fa-user-circle" style={{ marginRight: '8px', color: '#ccc' }}></i>
                        {donation.name === 'anonymous' ? t('donatePage.anonymous') : donation.name}
                      </span>
                      <div className="pf-donation-meta">
                        <span className="pf-donation-amount">€{donation.amount}</span>
                        <span className="pf-donation-time">{formatDate(donation.date)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CAROUSEL */}
        <div
          className="bg-light-custom block-padding"
          style={{ borderRadius: '30px', margin: '40px 0' }}
        >
          <div className="container">
            <div className="row text-center mb-5">
              <div className="col-md-12">
                <h2 className="section-title">
                  {t('donatePage.where_is_the_money_used')}
                </h2>
              </div>
            </div>

            <div
              id="owl-team2"
              className="owl-carousel owl-theme"
              ref={carouselRef}
            >
              {donate.map((service, idx) => (
                <div className="p-3" key={idx}>
                  <div className="pf-service-box">
                    <div className="pf-service-icon-wrap">
                      <i className={service.icon}></i>
                    </div>
                    <div className="pf-service-content mt-3">
                      <h5 style={{ fontWeight: '700' }}>{t(service.title)}</h5>
                      <p className="text-muted mt-2">
                        {t(service.description)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {showSuccess && (
        <div className="pf-success-overlay">
          <div className="pf-success-modal">
            <div className="pf-success-icon">
              <i className="fas fa-heart"></i>
            </div>
            <h2 className="pf-success-title">{t('donatePage.thank_you')}</h2>
            <p className="pf-success-message">
              {t('donatePage.donation_success').replace('{name}', successName)}
            </p>
            <button
              className="pf-success-close"
              onClick={() => setShowSuccess(false)}
            >
              {t('donatePage.close_modal')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Donate;
